import { useSyncExternalStore } from "react";

/**
 * How the app looks on this device: two choices, both made in Settings →
 * General, both kept in localStorage like the pane widths rather than on the
 * host. The theme is dark or light (dark is the default, as Grok Bot's is);
 * the style is calm (the default) or one of the others below. Each is an
 * attribute on the root element, and styles/tokens.css holds every value
 * either one changes.
 */
export type Theme = "dark" | "light";
export type Style = "calm" | "tactical" | "industrial" | "vintage" | "stellar" | "terminal" | "studio" | "glass";

const STYLES: readonly string[] = ["calm", "tactical", "industrial", "vintage", "stellar", "terminal", "studio", "glass"] satisfies readonly Style[];

const THEME_KEY = "ark-bot.theme";
const STYLE_KEY = "ark-bot.style";

/**
 * The colour the browser paints its own bar in: each palette's chrome
 * (styles/tokens.css), or, where the chrome is see-through (glass), the solid
 * colour under its ground, since a browser bar cannot be see-through.
 */
export const BAR_COLOR: Record<Style, Record<Theme, string>> = {
  calm: { dark: "#121315", light: "#f1f0ec" },
  tactical: { dark: "#090a0c", light: "#f5f4f0" },
  industrial: { dark: "#0f1012", light: "#f4f5f5" },
  vintage: { dark: "#0f1311", light: "#f2ead8" },
  stellar: { dark: "#0a0e1d", light: "#f6f7fb" },
  terminal: { dark: "#070b08", light: "#e8ecdf" },
  studio: { dark: "#161615", light: "#f6f5f2" },
  glass: { dark: "#0d1017", light: "#eef1f6" },
};

function read(key: string, storage: Pick<Storage, "getItem"> | undefined): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** The stored theme; anything else, including nothing stored, is dark. */
export function readTheme(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): Theme {
  return read(THEME_KEY, storage) === "light" ? "light" : "dark";
}

/** The stored style; anything else, including nothing stored, is calm. */
export function readStyle(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): Style {
  const stored = read(STYLE_KEY, storage);
  return stored != null && STYLES.includes(stored) ? (stored as Style) : "calm";
}

const listeners = new Set<() => void>();
let theme: Theme = typeof document === "undefined" ? "dark" : readTheme();
let style: Style = typeof document === "undefined" ? "calm" : readStyle();

/** Puts both choices on the page: the stylesheet keys off `data-theme` and `data-style` on the root element. */
export function applyLook(next: { readonly theme: Theme; readonly style: Style }): void {
  theme = next.theme;
  style = next.style;
  document.documentElement.dataset.theme = theme;
  document.documentElement.dataset.style = style;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", BAR_COLOR[style][theme]);
}

function choose(key: string, value: string, next: { readonly theme: Theme; readonly style: Style }): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage refused (a private window): the choice holds until the page is closed.
  }
  applyLook(next);
  for (const listener of listeners) listener();
}

/** Picks a theme for this device: saved, applied at once, and every reader told. */
export function setTheme(next: Theme): void {
  choose(THEME_KEY, next, { theme: next, style });
}

/** Picks a style for this device, the same way. */
export function setStyle(next: Style): void {
  choose(STYLE_KEY, next, { theme, style: next });
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, () => theme);
}

export function useStyle(): Style {
  return useSyncExternalStore(subscribe, () => style);
}
